/**
 * Phase J-UX — Journal search + collapsible filters.
 *
 * Layout:
 *   [   search box                     ] [Filters ▾] [count]
 *
 *   When Filters expands, a tidy panel below shows:
 *     • Visibility   (All / Shared / Private / Linked)
 *     • Mood         (Very good / Good / Neutral / Low / Very low / Not recorded — multi-select)
 *     • Children     (filter via global ChildScope, no chip here to avoid duplication)
 *     • Date range   (from / to)
 *
 * All filter state lives in the URL so deep-links + back button
 * just work. Parent passes in `searchParams` / `setSearchParams`
 * from `useSearchParams()` so this component stays controlled.
 */
import React, { useState } from "react";
import { Search, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MOOD_META } from "@/components/journal/MoodPicker";

const MOOD_BY_VALUE = Object.fromEntries(MOOD_META.map((m) => [m.value, m]));

const VISIBILITY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "shared", label: "Shared" },
  { value: "private", label: "Private" },
  { value: "linked", label: "Linked to calendar" },
];

const MOOD_FILTER_OPTIONS = [
  { value: "5", label: "Very good" },
  { value: "4", label: "Good" },
  { value: "3", label: "Neutral" },
  { value: "2", label: "Low" },
  { value: "1", label: "Very low" },
  { value: "unset", label: "Not recorded" },
];

export function JournalSearchFilters({
  searchParams,
  setSearchParams,
  resultCount,
  initialOpen = false,
}) {
  const [open, setOpen] = useState(initialOpen);

  const q = searchParams.get("q") || "";
  const filter = (searchParams.get("filter") || "all").toLowerCase();
  const moodRaw = (searchParams.get("mood") || "").trim();
  const moodSet = new Set(moodRaw ? moodRaw.split(",") : []);
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v == null || v === false) next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: false });
  };

  const toggleMood = (val) => {
    const next = new URLSearchParams(searchParams);
    const cur = new Set(moodSet);
    if (cur.has(val)) cur.delete(val);
    else cur.add(val);
    if (cur.size === 0) next.delete("mood");
    else next.set("mood", Array.from(cur).join(","));
    setSearchParams(next, { replace: false });
  };

  const activeFilterCount = [
    filter !== "all" ? 1 : 0,
    moodSet.size > 0 ? 1 : 0,
    from ? 1 : 0,
    to ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    const next = new URLSearchParams(searchParams);
    ["filter", "mood", "from", "to", "q"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: false });
  };

  return (
    <div className="space-y-3" data-testid="journal-search-filters">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F828A] pointer-events-none"
            strokeWidth={1.7}
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Search titles, content, tags, locations…"
            className="pl-9 pr-9 h-10 border-[#D9E1E3] rounded-sm"
            data-testid="journal-search-input"
          />
          {q ? (
            <button
              type="button"
              onClick={() => setParam("q", "")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-[#6F828A] hover:text-[#2E3A3F]"
              aria-label="Clear search"
              data-testid="journal-search-clear"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.7} />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen((v) => !v)}
            className="border-[#D9E1E3] rounded-sm h-10 px-3 inline-flex items-center gap-1.5"
            data-testid="journal-filters-toggle"
            aria-expanded={open}
          >
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.7} />
            Filters
            {activeFilterCount > 0 ? (
              <span
                className="text-[10px] uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm bg-[#1F9FA4] text-white font-semibold"
                data-testid="journal-filter-count-pill"
              >
                {activeFilterCount}
              </span>
            ) : null}
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
              strokeWidth={1.7}
            />
          </Button>
          <span
            className="text-xs text-[#6F828A] whitespace-nowrap"
            data-testid="journal-result-count"
          >
            {resultCount} {resultCount === 1 ? "entry" : "entries"}
          </span>
        </div>
      </div>

      {open ? (
        <div
          className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-4"
          data-testid="journal-filter-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
              Refine
            </div>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-[#6F828A] underline hover:text-[#2E3A3F]"
                data-testid="journal-filters-clear-all"
              >
                Clear all
              </button>
            ) : null}
          </div>

          {/* Visibility */}
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A]">
              Visibility
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {VISIBILITY_OPTIONS.map((o) => {
                const active = filter === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setParam("filter", o.value === "all" ? "" : o.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                      active
                        ? "bg-[#1F9FA4] border-[#1F9FA4] text-white"
                        : "bg-white border-[#D9E1E3] text-[#6F828A] hover:border-[#A7D8DA] hover:text-[#116B6E]"
                    }`}
                    data-testid={`journal-visibility-${o.value}`}
                    aria-pressed={active}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mood */}
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A]">
              Mood
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {MOOD_FILTER_OPTIONS.map((o) => {
                const active = moodSet.has(o.value);
                const meta = MOOD_BY_VALUE[Number(o.value)];
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggleMood(o.value)}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                      active
                        ? "bg-[#0B2E32] text-white border-[#0B2E32]"
                        : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F4FAFA]"
                    }`}
                    data-testid={`journal-mood-${o.value}`}
                    aria-pressed={active}
                  >
                    {meta?.Icon && !active ? (
                      <meta.Icon
                        className="w-3 h-3 inline mr-1 -mt-0.5"
                        style={{ color: meta.color }}
                        strokeWidth={1.8}
                      />
                    ) : null}
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date range */}
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A]">
              Date range
            </Label>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3 sm:items-end">
              <div>
                <Label className="text-xs text-[#6F828A]">From</Label>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => setParam("from", e.target.value)}
                  className="w-full sm:w-[170px] border-[#D9E1E3] rounded-sm"
                  data-testid="journal-from-date"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6F828A]">To</Label>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => setParam("to", e.target.value)}
                  className="w-full sm:w-[170px] border-[#D9E1E3] rounded-sm"
                  data-testid="journal-to-date"
                />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[#6F828A]">
            Filters update the list instantly. Use the search box to find
            entries by title, content, location, tag or linked-record name.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default JournalSearchFilters;
